import { useCallback, useEffect, useMemo, useState } from "react";
import { JUMP_TOOLTIP_DURATION_MS } from "../../config/constants";
import { clampFramePosition, getFrameProgressPercentage } from "./utils";

type UseTrackReviewStateArgs = {
  taskId: string | null;
  totalFrames: number;
  clearestFramePosition: number | null;
};

export function useTrackReviewState({
  taskId,
  totalFrames,
  clearestFramePosition,
}: UseTrackReviewStateArgs) {
  const [currentFrame, setCurrentFrame] = useState(1);
  const [isTrackingConfirmed, setIsTrackingConfirmed] = useState(false);
  // Choosing the end frame, after the labeler answered that the bounding box jumped: they pick the
  // last frame where it's still on the right product, and end the track there.
  const [isChoosingEndFrame, setIsChoosingEndFrame] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [showJumpTooltip, setShowJumpTooltip] = useState(false);
  const [jumpTooltipFrame, setJumpTooltipFrame] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [endFrame, setEndFrame] = useState(1);

  const clampedCurrentFrame = clampFramePosition(currentFrame, totalFrames);
  const safeTotalFrames = Math.max(totalFrames, 1);
  const effectiveEndFrame = clampFramePosition(endFrame, totalFrames);

  useEffect(() => {
    setCurrentFrame(1);
    setEndFrame(Math.max(totalFrames, 1));
    setIsTrackingConfirmed(false);
    setIsChoosingEndFrame(false);
    setHasReachedEnd(false);
    setShowJumpTooltip(false);
    setJumpTooltipFrame(null);
    setSelectedProduct("");
  }, [taskId, totalFrames]);

  useEffect(() => {
    if (totalFrames === 0) {
      setCurrentFrame(1);
      setEndFrame(1);
      return;
    }

    setCurrentFrame((previousFrame) =>
      clampFramePosition(previousFrame, totalFrames),
    );
    setEndFrame((previousFrame) =>
      clampFramePosition(previousFrame, totalFrames),
    );
  }, [totalFrames]);

  useEffect(() => {
    if (totalFrames > 0 && clampedCurrentFrame === totalFrames) {
      setHasReachedEnd(true);
    }
  }, [clampedCurrentFrame, totalFrames]);

  useEffect(() => {
    if (!showJumpTooltip) {
      return;
    }

    const timer = window.setTimeout(
      () => setShowJumpTooltip(false),
      JUMP_TOOLTIP_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [showJumpTooltip]);

  const progressPercentage = useMemo(
    () => getFrameProgressPercentage(clampedCurrentFrame, totalFrames),
    [clampedCurrentFrame, totalFrames],
  );

  const validPercentage = useMemo(
    () => getFrameProgressPercentage(effectiveEndFrame, totalFrames),
    [effectiveEndFrame, totalFrames],
  );

  const jumpTooltipPercentage = useMemo(() => {
    if (jumpTooltipFrame === null) {
      return progressPercentage;
    }

    const tooltipFrame = clampFramePosition(jumpTooltipFrame, totalFrames);
    return getFrameProgressPercentage(tooltipFrame, totalFrames);
  }, [jumpTooltipFrame, progressPercentage, totalFrames]);

  const clearTransientUiState = useCallback(() => {
    setShowJumpTooltip(false);
    setJumpTooltipFrame(null);
  }, []);

  const handleNext = useCallback(
    (step = 1) => {
      if (totalFrames === 0) {
        return;
      }

      setCurrentFrame((previousFrame) =>
        clampFramePosition(previousFrame + step, totalFrames),
      );
    },
    [totalFrames],
  );

  const handlePrevious = useCallback(
    (step = 1) => {
      if (totalFrames === 0) {
        return;
      }

      setCurrentFrame((previousFrame) =>
        clampFramePosition(previousFrame - step, totalFrames),
      );
    },
    [totalFrames],
  );

  const handleFrameSliderChange = useCallback(
    (value: string) => {
      setCurrentFrame(clampFramePosition(Number(value), totalFrames));
    },
    [totalFrames],
  );

  const moveToPostConfirmationFrame = useCallback(
    (maxFrame: number) => {
      if (clearestFramePosition === null) {
        return;
      }

      const targetFrame = clampFramePosition(clearestFramePosition, maxFrame);
      setCurrentFrame(targetFrame);
      setJumpTooltipFrame(targetFrame);
      setShowJumpTooltip(true);
    },
    [clearestFramePosition],
  );

  const handleConfirmTracking = useCallback(() => {
    if (totalFrames === 0) {
      return;
    }

    setEndFrame(totalFrames);
    setIsTrackingConfirmed(true);
    moveToPostConfirmationFrame(totalFrames);
  }, [moveToPostConfirmationFrame, totalFrames]);

  const startChoosingEndFrame = useCallback(() => {
    if (totalFrames <= 1 || !hasReachedEnd) {
      return;
    }

    setIsChoosingEndFrame(true);
  }, [hasReachedEnd, totalFrames]);

  const cancelChoosingEndFrame = useCallback(() => {
    setIsChoosingEndFrame(false);
  }, []);

  // Ends the track just before the current frame, only while choosing the end frame: the labeler
  // stops on the first frame where the highlight is on another product, so the frames before it
  // are kept. Not on frame 1, which has nothing before it.
  const handleEndTrack = useCallback(() => {
    if (totalFrames === 0 || !isChoosingEndFrame || clampedCurrentFrame <= 1) {
      return;
    }

    const cutFrame = clampedCurrentFrame - 1;
    setEndFrame(cutFrame);
    setIsChoosingEndFrame(false);
    setIsTrackingConfirmed(true);
    moveToPostConfirmationFrame(cutFrame);
  }, [
    clampedCurrentFrame,
    isChoosingEndFrame,
    moveToPostConfirmationFrame,
    totalFrames,
  ]);

  const resetTask = useCallback(() => {
    setCurrentFrame(1);
    setEndFrame(Math.max(totalFrames, 1));
    setIsTrackingConfirmed(false);
    setIsChoosingEndFrame(false);
    setHasReachedEnd(false);
    setShowJumpTooltip(false);
    setJumpTooltipFrame(null);
    setSelectedProduct("");
  }, [totalFrames]);

  const isPrimaryConfirmDisabled = totalFrames === 0 || !hasReachedEnd;

  return {
    cancelChoosingEndFrame,
    clearTransientUiState,
    clampedCurrentFrame,
    effectiveEndFrame,
    handleConfirmTracking,
    handleEndTrack,
    handleFrameSliderChange,
    handleNext,
    handlePrevious,
    hasReachedEnd,
    isChoosingEndFrame,
    isPrimaryConfirmDisabled,
    isTrackingConfirmed,
    jumpTooltipPercentage,
    progressPercentage,
    resetTask,
    safeTotalFrames,
    selectedProduct,
    setSelectedProduct,
    showJumpTooltip,
    startChoosingEndFrame,
    validPercentage,
  };
}
